import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  deriveWrappingKey,
  generateKeyPair,
  exportPublicKey,
  wrapPrivateKey,
  unwrapPrivateKey,
  deriveEscrowKey,
  wrapKeyForEscrow,
} from '../utils/fieldEncryption';

const AuthContext = createContext(null);

// Cross-tab logout signalling: when one tab logs out, every other tab of this
// app drops its session immediately instead of lingering in a stale logged-in
// state. Guarded for environments without BroadcastChannel.
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('paradigm-auth') : null;

// THE SESSION HYDRATES WARM (owner call 2026-08-22, the nucleus fix): the
// "Loading…" wall on every refresh was this provider blocking first paint on
// a /auth/me round trip. The last confirmed identity is snapshotted here, so
// a load that still holds a token paints the app immediately and the fetch
// revalidates underneath — a 401 still tears the session down exactly as
// before. Identity only; no tokens beyond what was already stored.
const CACHED_USER_KEY = 'developer:me';
const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(CACHED_USER_KEY)); } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => (localStorage.getItem('token') ? readCachedUser() : null));
  const [loading, setLoading] = useState(() => !(localStorage.getItem('token') && readCachedUser()));
  const [isDeveloper, setIsDeveloper] = useState(false);

  // encKeys holds in-memory key material — never persisted to localStorage/sessionStorage.
  // Shape: { publicKey: CryptoKey, privateKey: CryptoKey, wrappingKey: CryptoKey }
  //   publicKey   → encrypt fields (same key available to 3PAs via public endpoint)
  //   privateKey  → decrypt v2 ciphertext
  //   wrappingKey → decrypt legacy v1 ciphertext + protect stored private key
  const [encKeys, setEncKeys] = useState(null);

  // pendingRecoveryCodes: plaintext codes shown once after first-login key setup.
  // Cleared by calling clearPendingRecoveryCodes() once the user has acknowledged them.
  const [pendingRecoveryCodes, setPendingRecoveryCodes] = useState(null);
  // Whether existing recovery codes have escrow entries (false → show warning in Settings).
  const [recoveryCodesHaveEscrow, setRecoveryCodesHaveEscrow] = useState(true);

  // Single source of truth for tearing down local auth state. Used by both the
  // user-initiated logout() and the forced/expiry path so they never drift.
  const clearLocalAuth = useCallback(() => {
    api.setToken(null);
    api.setRefreshToken(null);
    api.setApiKey(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('apiKey');
    localStorage.removeItem('isDeveloper');
    localStorage.removeItem(CACHED_USER_KEY);
    sessionStorage.removeItem('activeSession');
    setUser(null);
    setIsDeveloper(false);
    setEncKeys(null); // Wipe all key material from memory
  }, []);

  // Handle forced logout (when auth expires or the session is revoked elsewhere)
  const forceLogout = useCallback((showExpiredMessage = true) => {
    clearLocalAuth();
    window.location.href = showExpiredMessage ? '/login?expired=true' : '/login';
  }, [clearLocalAuth]);

  const fetchUser = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      try { localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData)); } catch { /* quota */ }
    } catch (error) {
      // Only a REJECTED session tears down (401/403) — a transient failure
      // keeps the hydrated identity, or the warm boot would flash the app
      // and then bounce it to /login.
      if (error?.status === 401 || error?.status === 403 || error?.isAuthError) {
        api.setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem(CACHED_USER_KEY);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    // Developer mode retired — the app is always unified user mode, with dev tools
    // surfaced in the user sidebar. Clear any previously-stored flag.
    localStorage.removeItem('isDeveloper');

    if (token) {
      api.setToken(token);
      fetchUser();
    } else {
      setLoading(false);
    }

    const handleAuthExpired = (event) => {
      const showExpiredMessage = event.detail?.showExpiredMessage ?? true;
      forceLogout(showExpiredMessage);
    };
    window.addEventListener('auth:expired', handleAuthExpired);

    // Cross-tab: another tab logged out → drop this tab's session too.
    const handleChannelMessage = (event) => {
      if (event.data?.type === 'logout') forceLogout(false);
    };
    authChannel?.addEventListener('message', handleChannelMessage);

    // Idle poll: when the tab regains focus, confirm the session is still live.
    // A revoked session (logged out elsewhere / force-logged-out by an app)
    // returns 401 here, so we drop it without waiting for the next API call.
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      try {
        await api.getSessionStatus();
      } catch (e) {
        if (e?.status === 401) forceLogout(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
      authChannel?.removeEventListener('message', handleChannelMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [forceLogout, fetchUser]);

  const _setupEncKeys = async (password, userId, loginResp) => {
    try {
      const wrappingKey = await deriveWrappingKey(password, String(userId));
      const { enc_public_key, enc_private_key_wrapped } = loginResp;

      let privateKey, publicKey;

      if (enc_public_key && enc_private_key_wrapped) {
        // Existing key pair — unwrap private key in memory
        const pubBytes = Uint8Array.from(
          atob(enc_public_key.replace(/-/g, '+').replace(/_/g, '/')),
          c => c.charCodeAt(0)
        );
        publicKey = await crypto.subtle.importKey(
          'raw', pubBytes, { name: 'ECDH', namedCurve: 'P-256' }, true, []
        );
        privateKey = await unwrapPrivateKey(enc_private_key_wrapped, wrappingKey);
      } else {
        // First login — generate key pair, wrap private key, upload
        const kp = await generateKeyPair();
        privateKey = kp.privateKey;
        publicKey = kp.publicKey;

        const pubBytes = await exportPublicKey(publicKey);
        const wrapped = await wrapPrivateKey(privateKey, wrappingKey);
        const pubB64 = btoa(String.fromCharCode(...pubBytes))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

        // Derive escrow entries for each setup recovery code (returned on first login only)
        const setupCodes = loginResp.setup_recovery_codes || [];
        const recoveryKeyEscrow = await Promise.all(
          setupCodes.map(async ({ id, code }) => {
            const escrowKey = await deriveEscrowKey(code, String(userId));
            const encEscrow = await wrapKeyForEscrow(privateKey, escrowKey);
            return { code_id: id, enc_key_escrow: encEscrow };
          })
        );

        await api.request('/auth/enc-keys', {
          method: 'PUT',
          body: JSON.stringify({
            enc_public_key: pubB64,
            enc_private_key_wrapped: wrapped,
            recovery_key_escrow: recoveryKeyEscrow,
          }),
        });

        if (setupCodes.length) {
          setPendingRecoveryCodes(setupCodes.map(c => c.code));
        }
      }

      setRecoveryCodesHaveEscrow(loginResp.recovery_codes_have_escrow ?? true);
      setEncKeys({ publicKey, privateKey, wrappingKey });
    } catch (e) {
      console.warn('Failed to set up encryption keys:', e);
    }
  };

  const login = async (identifier, password, asDeveloper = false) => {
    const response = await api.login(identifier, password);

    const token = response.access_token || response.token;
    if (token) {
      api.setToken(token);
      localStorage.setItem('token', token);
      if (response.refresh_token) {
        api.setRefreshToken(response.refresh_token);
      }
      sessionStorage.setItem('activeSession', 'true');
    }

    if (response.user?.id) {
      await _setupEncKeys(password, response.user.id, response);
    }

    localStorage.removeItem('isDeveloper'); // mode retired — always user mode
    setIsDeveloper(false);
    await fetchUser();
    return response;
  };


  // Unified logout. scope='global' (default) ends every session for the user;
  // scope='app' ends only this app's session. The server call is best-effort;
  // local state is always cleared and other tabs are notified.
  const logout = useCallback(async ({ scope = 'global' } = {}) => {
    try {
      await api.logout(scope);
    } catch (error) {
      // best-effort — clear local state regardless
    } finally {
      clearLocalAuth();
      try { authChannel?.postMessage({ type: 'logout' }); } catch (e) { /* no-op */ }
    }
  }, [clearLocalAuth]);

  const isAdmin = !!(user && user.is_admin);

  const hasAccess = useCallback((scope) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.access?.[scope] !== false;
  }, [user]);

  const clearPendingRecoveryCodes = () => setPendingRecoveryCodes(null);

  const value = {
    user,
    loading,
    isDeveloper,
    isAdmin,
    hasAccess,
    encKeys,   // { publicKey, privateKey, wrappingKey }
    pendingRecoveryCodes,        // plaintext codes shown once after first-login key setup
    recoveryCodesHaveEscrow,     // false → Settings should prompt user to regenerate codes
    clearPendingRecoveryCodes,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
